export const ContextLabels = {
  activation: "ACTIVATION",
  enrgyPrice: "ENERGY_PRICE",
  enrgyPriceDay: "ENERGY_PRICE_DAY",
  enrgyPriceNight: "ENERGY_PRICE_NIGHT",
  blockingFee: "BLOCKING_FEE",
  blockingFeeDay: "BLOCKING_FEE_DAY",
  blockingFeeNight: "BLOCKING_FEE_NIGHT",
  blockingFeeCap: "BLOCKING_FEE_CAP",
  blockingFeeCapDay: "BLOCKING_FEE_CAP_DAY",
  blockingFeeCapNight: "BLOCKING_FEE_CAP_NIGHT",
};

// HELPERS

export const getTariffType = (tariff) => {
  return tariff["tariff_alt_text"]?.[0]?.text;
};

export const getActivationFeeFromTariff = (tariff) => {
  const tariffElementsList = tariff?.elements;

  // Activation Fee is the first FLAT element, which has no restrictions (i.e one-off charge)
  const activationFeeElement = tariffElementsList?.filter(
    (c) => c?.context_label === ContextLabels.activation,
  );

  const activationFeeComponent = activationFeeElement[0].price_components[0];

  // Apply the vat on the price
  const activationFeePriceWithVat =
    Number(activationFeeComponent?.price) *
    (1 + Number(activationFeeComponent?.vat) / 100);

  return {
    // Return activation price after VAT (rounded at 2 sig. figures)
    price: activationFeePriceWithVat?.toFixed(2) || null,
    unit: "€",
    restrictions: null,
  };
};

export const getEnergyFeeFromTariff = (tariff, contextLabel) => {
  const tariffElementsList = tariff?.elements;

  const energyFeeElement = tariffElementsList?.filter(
    (c) => c?.context_label === contextLabel,
  );

  const energyFeeComponent = energyFeeElement[0].price_components[0];

  // Apply the vat on the price
  const energyFeePriceWithVat =
    Number(energyFeeComponent?.price) *
    (1 + Number(energyFeeComponent?.vat) / 100);

  // Return price after VAT (rounded at 2 sig. figures)
  return {
    price: energyFeePriceWithVat?.toFixed(2) || null,
    unit: "€/kWh",
    restrictions: null,
  };
};

export const getBlockingFeeFromTariff = (tariff, contextLabel) => {
  const tariffElementsList = tariff?.elements;

  const blockingFeeElement = tariffElementsList?.filter(
    (c) => c?.context_label === contextLabel,
  );

  const blockingFeeComponent = blockingFeeElement[0].price_components[0];

  // Apply the vat on the price
  const blockingFeePriceWithVat =
    Number(blockingFeeComponent?.price) *
    (1 + Number(blockingFeeComponent?.vat) / 100);

  return {
    // Return price after VAT (rounded at 2 sig. figures)
    price: blockingFeePriceWithVat?.toFixed(2) || null,
    unit: "€/h",
    restrictions: {
      minDuration: blockingFeeElement?.[0]?.restrictions?.min_duration || null, // unit: seconds
      maxDuration: blockingFeeElement?.[0]?.restrictions?.max_duration || null, // unit: seconds
    },
  };
};

export const getBlockingFeeCapFromTariff = (tariff, contextLabel) => {
  const tariffElementsList = tariff?.elements;

  const blockingFeeCapElement = tariffElementsList?.filter(
    (c) => c?.context_label === contextLabel,
  );

  if (!blockingFeeCapElement?.length) {
    return { price: null, unit: "€", restrictions: { minDuration: null } };
  }

  const blockingFeeCapComponent = blockingFeeCapElement[0].price_components[0];

  // Apply the vat on the price
  const blockingFeePriceWithVat =
    Number(blockingFeeCapComponent?.price) *
    (1 + Number(blockingFeeCapComponent?.vat) / 100);

  return {
    // Return price after VAT (rounded at 2 sig. figures)
    price: blockingFeePriceWithVat?.toFixed(2) || null,
    unit: "€",
    restrictions: {
      minDuration: blockingFeeCapElement[0]?.restrictions?.min_duration, // unit: seconds
    },
  };
};

export const getDurationFromTariff = (tariff, contextLabel) => {
  // duration is same on all price components
  if (!contextLabel) {
    console.warn("Missing input context label on getDurationFromTariff");
    return;
  }

  const tariffElementsList = tariff?.elements;

  const energyFeeElement = tariffElementsList?.filter(
    (c) => c?.context_label === contextLabel,
  )[0];

  const startTimeString = energyFeeElement?.restrictions.start_time || ""; // format: 'HH:mm:ss'
  const endTimeString = energyFeeElement?.restrictions.end_time || ""; // format: 'HH:mm:ss'

  // format to HH:mm - HH:mm
  const formattedPeriodDuration = `${startTimeString.slice(
    0,
    startTimeString.length - 3,
  )} - ${endTimeString.slice(0, startTimeString.length - 3)}`;

  return formattedPeriodDuration;
};

// STATIC

export const getStaticPriceBreakdownFromTariff = (tariff) => {
  return {
    activationFee: getActivationFeeFromTariff(tariff), // remains the same, regardless of tariff type
    energyFee: getEnergyFeeFromTariff(tariff, ContextLabels.energyFee),
    blockingFee: getBlockingFeeFromTariff(tariff, ContextLabels.blockingFee),
    blockingFeeCap: getBlockingFeeCapFromTariff(
      tariff,
      ContextLabels.blockingFeeCap,
    ),
    periodDuration: getDurationFromTariff(tariff, ContextLabels.energyFee),
  };
};

// DAY_NIGHT

export const getDayPriceBreakdownFromTariff = (tariff) => {
  // Returns price breakdown for DAY period
  return {
    activationFee: getActivationFeeFromTariff(tariff), // remains the same, regardless of tariff type
    energyFee: getEnergyFeeFromTariff(tariff, ContextLabels.energyFeeDay),
    blockingFee: getBlockingFeeFromTariff(tariff, ContextLabels.blockingFeeDay),
    blockingFeeCap: getBlockingFeeCapFromTariff(
      tariff,
      ContextLabels.blockingFeeCapDay,
    ),
    periodDuration: getDurationFromTariff(tariff, ContextLabels.energyFeeDay),
  };
};

export const getNightPriceBreakdownFromTariff = (tariff) => {
  return {
    activationFee: getActivationFeeFromTariff(tariff), // remains the same, regardless of tariff type
    energyFee: getEnergyFeeFromTariff(tariff, ContextLabels.energyFeeNight),
    blockingFee: getBlockingFeeFromTariff(
      tariff,
      ContextLabels.blockingFeeNight,
    ),
    blockingFeeCap: getBlockingFeeCapFromTariff(
      tariff,
      ContextLabels.blockingFeeCapNight,
    ),
    periodDuration: getDurationFromTariff(tariff, ContextLabels.energyFeeNight),
  };
};

/** A tariff is "free" when all ENERGY price components have price === 0. */
export const isFreeGroup = (tariff) =>
  tariff.elements
    .filter((e) => e.price_components[0]?.type === "ENERGY")
    .every((e) => e.price_components[0]?.price === 0);

// COMMON
export const getPriceBreakdownFromTariff = (tariff, period = undefined) => {
  const tariffType = getTariffType(tariff);

  switch (tariffType) {
    case "DAY_NIGHT":
      return {
        day: getDayPriceBreakdownFromTariff(tariff),
        night: getNightPriceBreakdownFromTariff(tariff),
      };
    case "HOURLY":
      console.error("TODO");
      return;
    default:
      getStaticPriceBreakdownFromTariff(tariff);
  }
};
